import axios from 'axios';
import { getBackendUrl } from '../config';
import { triggerCookieProblemModal } from '../components/CookieProblemModal';
import { clearHeaderTooLargeModalShown, setHadCookieError } from '../utils/storageCleanup';
import { readToken, refreshAccessToken, handleSessionExpired } from './authSession';

/** Error code when Nginx returns "Request Header Or Cookie Too Large" (400/413/431). */
export const ERR_HEADER_OR_COOKIE_TOO_LARGE = 'ERR_HEADER_OR_COOKIE_TOO_LARGE';

const NGINX_HEADER_TOO_LARGE_PATTERN = /request header or cookie too large/i;

function isHeaderOrCookieTooLargeResponse(response) {
  if (!response) return false;
  const status = response.status;
  if (status !== 400 && status !== 413 && status !== 431) return false;
  const data = response.data;
  const body = typeof data === 'string' ? data : (data && String(data)) || '';
  const isHtml = /<\s*html\s*[\s>]/i.test(body) || /<\s*!DOCTYPE\s+/i.test(body);
  return isHtml && NGINX_HEADER_TOO_LARGE_PATTERN.test(body);
}

let _cachedBaseURL;
function resolveBaseURL() {
  if (_cachedBaseURL !== undefined) return _cachedBaseURL;
  const runtime = getBackendUrl() || process.env.REACT_APP_BACKEND_URL;
  _cachedBaseURL = runtime ? String(runtime).replace(/\/$/, '') : null;
  return _cachedBaseURL;
}

const api = axios.create({});

export const openApi = axios.create({});

function withRuntimeBaseURL(config) {
  const baseURL = resolveBaseURL();
  if (!baseURL || config?.baseURL) return config;
  return { ...config, baseURL };
}

api.interceptors.request.use(withRuntimeBaseURL);
openApi.interceptors.request.use(withRuntimeBaseURL);

/**
 * Anexa o access token. Registrado em escopo de módulo (e não num useEffect do
 * useAuth) porque providers filhos disparam requests antes dos effects do pai
 * rodarem — essas requests saíam sem Authorization e levavam 401.
 */
api.interceptors.request.use((config) => {
  const token = readToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const response = error?.response;

    if (isHeaderOrCookieTooLargeResponse(response)) {
      const err = error;
      err.isHeaderOrCookieTooLarge = true;
      err.code = ERR_HEADER_OR_COOKIE_TOO_LARGE;
      setHadCookieError();
      triggerCookieProblemModal();
      return Promise.reject(error);
    }

    const status = response?.status;
    const originalRequest = error?.config;

    // 403: bloqueio comercial — o usuário segue autenticado. O PlanPermissionsContext
    // e o layout leem user.company.status e exibem a tela adequada.
    if (status === 403) {
      return Promise.reject(error);
    }

    // 402: empresa inativa/trial vencido (checkCompanyActive no backend).
    if (status === 402) {
      const path = window.location.pathname;
      if (path !== '/account-blocked' && !path.startsWith('/subscription')) {
        window.location.assign('/account-blocked');
      }
      return Promise.reject(error);
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Single-flight: requests paralelas com 401 dividem um único refresh.
      const newToken = await refreshAccessToken();

      if (newToken) {
        // Sem setar o header aqui: o refresh já persistiu o token novo e o
        // interceptor de request o relê no retry. Um único lugar monta o header.
        return api(originalRequest);
      }

      handleSessionExpired();
    }

    return Promise.reject(error);
  }
);

if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.__pingochatSimulateHeaderTooLarge = () => {
    clearHeaderTooLargeModalShown();
    triggerCookieProblemModal();
  };
}

export default api;
